import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Eataly Library — Tag / Badge (Figma: node 7:2690).
 * Use `hierarchy` × `schema` for design-system presets.
 * Legacy `variant` values map to the same presets for compatibility with shadcn-style call sites.
 */
const badgeVariants = cva(
  "inline-flex items-center justify-center gap-1 rounded-[4px] border border-transparent px-1 py-px label-m w-fit whitespace-nowrap shrink-0 [&>svg]:shrink-0 transition-[color,box-shadow,opacity] overflow-hidden focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:opacity-90 uppercase",
  {
    variants: {
      hierarchy: {
        primary: "",
        secondary: "",
      },
      schema: {
        default: "",
        neutral: "",
        positive: "",
        warning: "",
        danger: "",
      },
    },
    compoundVariants: [
      {
        hierarchy: "primary",
        schema: "default",
        class:
          "bg-[var(--sand-90)] text-[var(--neutral-10)]",
      },
      {
        hierarchy: "primary",
        schema: "neutral",
        class: "bg-[var(--status-neutral-20)] text-neutral-white",
      },
      {
        hierarchy: "primary",
        schema: "positive",
        class: "bg-[var(--status-green-40)] text-neutral-white",
      },
      {
        hierarchy: "primary",
        schema: "warning",
        class: "bg-[var(--status-orange-40)] text-neutral-white",
      },
      {
        hierarchy: "primary",
        schema: "danger",
        class: "bg-[var(--status-red-40)] text-neutral-white",
      },
      {
        hierarchy: "secondary",
        schema: "default",
        class:
          "bg-[var(--brown-90)] text-[var(--brown-10)]",
      },
      {
        hierarchy: "secondary",
        schema: "neutral",
        class:
          "bg-[var(--sand-95)] text-[var(--status-neutral-10)]",
      },
      {
        hierarchy: "secondary",
        schema: "positive",
        class:
          "bg-[var(--status-green-98)] text-[var(--status-green-10)]",
      },
      {
        hierarchy: "secondary",
        schema: "warning",
        class:
          "bg-[var(--status-orange-95)] text-[var(--status-orange-10)]",
      },
      {
        hierarchy: "secondary",
        schema: "danger",
        class:
          "bg-[var(--status-red-95)] text-[var(--status-red-10)]",
      },
    ],
    defaultVariants: {
      hierarchy: "primary",
      schema: "default",
    },
  }
)

const legacyOutlineBadgeClasses =
  "border-border bg-background text-foreground shadow-none [a&]:hover:bg-accent [a&]:hover:text-accent-foreground [a&]:hover:opacity-100"

const LEGACY_VARIANT_TO_FIGMA = {
  default: { hierarchy: "primary" as const, schema: "default" as const },
  secondary: { hierarchy: "secondary" as const, schema: "default" as const },
  destructive: { hierarchy: "primary" as const, schema: "danger" as const },
} as const

type LegacyBadgeVariant = keyof typeof LEGACY_VARIANT_TO_FIGMA | "outline"

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** @deprecated Prefer `hierarchy` and `schema` (Eataly Tag presets). */
    variant?: LegacyBadgeVariant
  }

function Badge({
  className,
  hierarchy,
  schema,
  variant: legacyVariant,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span"

  const useLegacyMapping =
    legacyVariant !== undefined &&
    hierarchy === undefined &&
    schema === undefined

  const isLegacyOutline = useLegacyMapping && legacyVariant === "outline"

  const legacyMapped =
    useLegacyMapping && legacyVariant !== "outline"
      ? LEGACY_VARIANT_TO_FIGMA[legacyVariant]
      : undefined

  const resolvedHierarchy = hierarchy ?? legacyMapped?.hierarchy ?? "primary"
  const resolvedSchema = schema ?? legacyMapped?.schema ?? "default"

  return (
    <Comp
      data-slot="badge"
      className={cn(
        isLegacyOutline
          ? legacyOutlineBadgeClasses
          : badgeVariants({
              hierarchy: resolvedHierarchy,
              schema: resolvedSchema,
            }),
        className
      )}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
export type { BadgeProps }
